// T2: Live intercom operations queue.
//
// Covers Stitch v2 design `intercom_operations_monitoring` (PNG-only;
// layout derived from the mockup): three stacked sections — ringing
// queue at top, in-call active set in the middle, and recent history
// at the bottom. Operators click "Answer" on a ringing row to claim
// it (atomic — concurrent answers get 409 + UI rollback).

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import type { IntercomCall } from '@ng-unifi/domain';
import { useIntercomCalls, statusLabel, statusTone } from '../lib/intercomCalls.js';
import { ApiError } from '../lib/api.js';

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString();
}

function CallRow({
  call,
  onAnswer,
  onEnd,
  busy,
}: {
  call: IntercomCall;
  onAnswer?: (id: string) => void;
  onEnd?: (id: string) => void;
  busy: boolean;
}) {
  const navigate = useNavigate();
  return (
    <li className="flex items-center gap-3 py-3">
      <Badge tone={statusTone(call.status)}>{statusLabel(call.status)}</Badge>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">
          Callbox {call.callbox_id.slice(0, 8)}
        </p>
        <p className="text-sm text-on-surface-variant">
          {relativeTime(call.initiated_at)}
          {call.answered_at ? ` · answered ${relativeTime(call.answered_at)}` : ''}
        </p>
      </div>
      {onAnswer && call.status === 'queued' ? (
        <Button onClick={() => onAnswer(call.id)} disabled={busy}>
          <Icon name="call" /> Answer
        </Button>
      ) : null}
      {onEnd && call.status === 'answered' ? (
        <Button variant="danger" onClick={() => onEnd(call.id)} disabled={busy}>
          <Icon name="call_end" /> End
        </Button>
      ) : null}
      <Button
        variant="ghost"
        onClick={() =>
          navigate(
            call.status === 'queued'
              ? `/intercom/incoming/${call.id}`
              : `/intercom/active/${call.id}`,
          )
        }
      >
        Open
      </Button>
    </li>
  );
}

export function IntercomOperationsPage() {
  const { calls, error, loading, answer, end, refresh } = useIntercomCalls();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleAnswer(id: string) {
    setActionErr(null);
    setBusy(true);
    try {
      await answer(id);
    } catch (e) {
      setActionErr(
        e instanceof ApiError
          ? e.code === 'conflict'
            ? 'Another operator already answered this call.'
            : e.message
          : 'Could not answer call',
      );
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd(id: string) {
    setActionErr(null);
    setBusy(true);
    try {
      await end(id, { reason: 'operator_ended' });
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'Could not end call');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const queued = (calls ?? []).filter((c) => c.status === 'queued');
  const active = (calls ?? []).filter((c) => c.status === 'answered');
  const history = (calls ?? []).filter(
    (c) => c.status === 'ended' || c.status === 'missed',
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Intercom operations</h1>
          <p className="text-on-surface-variant">
            Live queue across every callbox in this property. Auto-refreshes every
            5 seconds.
          </p>
        </div>
        <Link to="/intercom" className="text-primary underline self-center">
          Management hub →
        </Link>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}
      {actionErr ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {actionErr}
        </p>
      ) : null}

      <section>
        <h2 className="text-lg font-medium mb-2">
          Ringing <Badge tone="warning">{queued.length}</Badge>
        </h2>
        <Card>
          <CardBody>
            {queued.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No calls waiting.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {queued.map((c) => (
                  <CallRow key={c.id} call={c} onAnswer={handleAnswer} busy={busy} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-2">
          In call <Badge tone="success">{active.length}</Badge>
        </h2>
        <Card>
          <CardBody>
            {active.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No active calls.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {active.map((c) => (
                  <CallRow key={c.id} call={c} onEnd={handleEnd} busy={busy} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-2">Recent history</h2>
        <Card>
          <CardBody>
            {history.length === 0 ? (
              <p className="py-3 text-on-surface-variant">No history yet.</p>
            ) : (
              <ul className="divide-y divide-outline-variant">
                {history.slice(0, 20).map((c) => (
                  <CallRow key={c.id} call={c} busy={busy} />
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
